import { TableCell, TableRow } from "@comps/ui/table";
import { columnAggregation } from "@dto/fields";
import { Line } from "@dto/records";
import { FieldColumn } from "@dto/records";
import { getLightOpaqueColor } from "@utils/colors";
import { summaryAggregationsByColumns, summaryCellRadius, summaryValueToString } from "@utils/summaries";
import { Column, Table } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useModelStore } from "src/store";

interface SummaryTableRowProps {
    table: Table<Line>,
    fieldColumns: FieldColumn[],
    summaries: (any | null)[],
}

// SummaryTableRow is the sticky totals row at the bottom of the grid: the
// summarized cells form grey bands (contiguous cells merge, rounded at the
// band extremities) showing the view summaries over the whole result set; the
// other cells stay transparent. The aggregation type shows as a tooltip.
//
// The row sticks to the bottom edge of the scroll container while scrolling
// (the add-record row, also sticky at the bottom, overlays it on the left) and
// only settles at its natural position, above the add-record row, once the
// grid is scrolled to the end: sticking any higher would leave the bands
// floating over a data row.
function SummaryTableRow({ table, fieldColumns, summaries }: SummaryTableRowProps) {

    const { t } = useTranslation();
    const selectedView = useModelStore((state) => state.selectedView);
    const fieldByKeys = useModelStore((state) => state.fieldByKeys);
    const wsColor = useModelStore((state) => state.wsColor);
    // Same tint as wsLightColor but OPAQUE (composited over white): the row is
    // sticky, a translucent background would let scrolling rows show through.
    const bandColor = getLightOpaqueColor(wsColor);

    const aggregations = summaryAggregationsByColumns(selectedView?.summaries, fieldColumns);
    const cells = fieldColumns.map((fieldColumn, index) => {
        const aggregation = aggregations[index];
        const formatted = aggregation ? summaryValueToString(summaries[index], fieldColumn, aggregation, columnAggregation(fieldColumn.field, selectedView?.aggregations, fieldColumn.pathKey), selectedView?.formats, fieldByKeys, t) : "";
        return {
            aggregation,
            formatted,
            summarized: aggregation != null && formatted !== "",
        };
    });

    const cellStyle = (column: Column<Line, unknown>, fieldIndex: number): React.CSSProperties => {
        const isPinned = column.getIsPinned();
        const cell = fieldIndex >= 0 ? cells[fieldIndex] : undefined;
        const summarized = cell?.summarized ?? false;
        return {
            position: isPinned ? "sticky" : "relative",
            left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
            zIndex: isPinned ? 19 : undefined,
            background: summarized ? bandColor : "transparent",
            borderRadius: summaryCellRadius(summarized, cells[fieldIndex - 1]?.summarized ?? false, cells[fieldIndex + 1]?.summarized ?? false),
            width: column.getSize(),
            color: wsColor,
        };
    };

    // Leaf columns come in table order: the group shift columns and the select
    // column first, then one column per fieldColumn (index-aligned with the
    // summaries values).
    let fieldIndex = -1;
    return (
        <TableRow className="h-8 hover:bg-transparent" style={{ position: "sticky", bottom: 0, zIndex: 18 }}>
            {table.getVisibleLeafColumns().map((column) => {
                if (column.id === "select" || column.id.startsWith("group-")) {
                    return <TableCell key={"summary-" + column.id} className="p-0" style={cellStyle(column, -1)} />;
                }
                fieldIndex++;
                const cell = cells[fieldIndex];
                return (
                    <TableCell key={"summary-" + column.id} className="p-0" style={cellStyle(column, fieldIndex)}>
                        {cell.summarized &&
                            <div className="flex items-center justify-end px-2 h-8" title={t("field.aggregation.label." + cell.aggregation)}>
                                <span className="text-sm font-semibold">{cell.formatted}</span>
                            </div>
                        }
                    </TableCell>
                );
            })}
        </TableRow>
    );
}

export default SummaryTableRow;
